import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Device, DevicesService, DialogService, IDeviceData } from '@nrwl/common-lib';
import { ControllerApiService } from '@nrwl/test-setup';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'nrwl-jmep-reflector',
  templateUrl: './jmep-reflector.component.html',
  styleUrls: ['./jmep-reflector.component.scss']
})
export class JmepReflectorComponent implements OnInit, OnDestroy {
  @ViewChild('jmepReflectorForm', { static: true }) form: NgForm;

  deviceData: IDeviceData;
  deviceName: string;
  jmepSrvURL = null;
  jmepId = null;
  jmepName = null;
  jmepLicenseObj = null;
  jmepTaskName = 'probe-attributes';
  jmepLicenseEnabled = false;
  raiseConfirm = false;
  actionMsg = null;
  errorMsg = null;
  errorIcon = 'warning';
  formObj = {
    udpport: null
  };
  isLicenseLoaded = false;
  ipAddress = null;
  vlan = null;
  badIpValues = ['', '0.0.0.0', '255.255.255.255', '::/0'];
  attributeMapping = {
    sectionName: 'otherOptions',
    twampUDPPort: 'no_filter_twamp_udp_port',
    twampEnabled: 'no_filter_twamp_enabled',
    twampIgnoreIp: 'no_filter_twamp_ignore_ip'
  };
  defaultConfig = {
    udpport: '50000',
    udpStatus: false
  };
  isTestRunning = false;
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private readonly dialogService: DialogService,
    private readonly controllerApiService: ControllerApiService,
    private dialogRef: MatDialogRef<JmepReflectorComponent>,
    private readonly deviceService: DevicesService,
    @Inject(MAT_DIALOG_DATA) public data: Device
  ) {}

  ngOnInit(): void {
    this.deviceData = this.data.deviceData;
    this.deviceName = this.deviceData.nameId;
    this.isEnabledJMEPLicencse();
    this.jmepId = this.deviceData.capabilities.phy['jmep-id'];
    this.jmepName = this.deviceData.capabilities.system['hostname'];
    this.jmepSrvURL = this.deviceData.connections ? this.deviceData.connections[0].uri : '';
    this.ipAddress = this.getIpAddress();
    this.vlan =
      this.deviceData.capabilities.phy['test-vlan-id'] &&
      this.deviceData.capabilities.phy['test-vlan-id'] !== '-1'
        ? this.deviceData.capabilities.phy['test-vlan-id']
        : null;
    this.formObj.udpport = this.deviceData.capabilities.simpleTestConfig['twamp-udp-port']
      ? this.deviceData.capabilities.simpleTestConfig['twamp-udp-port']
      : this.defaultConfig.udpport;
    this.isTestRunning = this.deviceData.capabilities.simpleTestConfig['twampReflector']
      ? JSON.parse(this.deviceData.capabilities.simpleTestConfig['twampReflector'])
      : false;
  }

  ngOnDestroy() {
    this.dialogRef.close();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  isLoadingConfig(): boolean {
    return !this.isLicenseLoaded;
  }

  // handle reflector light port range check
  // port range 1-65535, except for 5060
  //
  handleReflectorLightPortRange(): void {
    if (
      this.formObj.udpport >= 1 &&
      this.formObj.udpport <= 65535 &&
      this.formObj.udpport != 5060
    ) {
      this.form.controls.udpport.setErrors(null);
    } else {
      this.form.controls.udpport.setErrors({ regex: 'wrong' });
    }
  }

  getIpAddress(): string {
    let ipAddress = '';
    const testIP = this.deviceData.capabilities.phy['test-ip']
      ? this.deviceData.capabilities.phy['test-ip']
      : null;
    const mgmtIP = this.deviceData.capabilities.phy['assigned-ip']
      ? this.deviceData.capabilities.phy['assigned-ip']
      : null;
    if (testIP && !this.badIpValues.includes(testIP)) {
      ipAddress = testIP;
    } else if (mgmtIP && !this.badIpValues.includes(mgmtIP)) {
      ipAddress = mgmtIP;
    }
    return ipAddress;
  }

  //eslint-disable-next-line
  getSectionAttibuteValue(jmepConfigData, sectionName, attributeName, defaultValue) {
    if (
      jmepConfigData &&
      jmepConfigData.value &&
      jmepConfigData.value.data &&
      jmepConfigData.value.data.length
    ) {
      const section = this.findByValue(jmepConfigData.value.data, 'name', sectionName);
      if (section && section.subAttributes) {
        const propertyObject = this.findByValue(section.subAttributes, 'id', attributeName);
        return propertyObject && propertyObject.value ? propertyObject.value : defaultValue;
      }
    }
    return defaultValue;
  }

  //eslint-disable-next-line
  findByValue(data, key, value): any {
    return data.find(item => item[key] === value);
  }

  startEndpoint(): void {
    //this.actionMsg = 'submitting test...';
    this.errorMsg = null;
    const configuration = this.getTestConfig('start');
    if (configuration !== null) {
      this.actionMsg = 'test accepted, now starting...';
      this.controllerApiService
        .setJMEPConfig({ reqData: configuration })
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(
          response => {
            if (response.status === 'success') {
              this.deviceService.getReflectorDevices();
              this.isTestRunning = true;
              this.setSuccessMessage('Reflector is running');
            } else {
              this.setErrorMessage('Could not start reflector: ' + response.data.message);
            }
          },
          () => {
            this.setErrorMessage('Could not start reflector ');
          }
        );
    }
  }

  //eslint-disable-next-line
  getTestConfig(testAction): any {
    const subAttributeArray = [];
    const lmapOptionsArray = {
      'config-attributes': {
        name: 'probe-attributes',
        option: [
          {
            name: this.jmepName,
            id: this.jmepId,
            value: {
              data: []
            }
          }
        ]
      }
    };
    const subAttribute1 = {
      id: '',
      value: ''
    };
    subAttribute1.id = this.attributeMapping.twampEnabled;
    subAttribute1.value = testAction === 'start' ? 'true' : 'false';
    subAttributeArray.push(subAttribute1);

    //Backend expecting udpPort value when stop reflector
    const subAttribute2 = {
      id: '',
      value: ''
    };
    subAttribute2.id = this.attributeMapping.twampUDPPort;
    subAttribute2.value = this.formObj.udpport;
    subAttributeArray.push(subAttribute2);

    // Backend expecting "no_filter_twamp_ignore_ip" field as mandatory for updating twamp reflector
    const subAttribute3 = {
      id: '',
      value: ''
    };
    subAttribute3.id = this.attributeMapping.twampIgnoreIp;
    subAttribute3.value = 'false';
    subAttributeArray.push(subAttribute3);

    const valueObj = {
      name: '',
      subAttributes: []
    };
    valueObj.name = this.attributeMapping.sectionName;
    valueObj.subAttributes = subAttributeArray;
    lmapOptionsArray['config-attributes'].option[0].value.data.push(valueObj);
    return lmapOptionsArray;
  }

  showStopDialog(): void {
    const param = {
      id: 'twamp_reflector-stop',
      alternateMsg: `Are you sure, you want to stop reflector?`,
      callBackService: this,
      callBackFuntion: () => {
        this.stopEndpoint();
        this.raiseConfirm = true;
      },
      callBackFnParams: {
        self: this
      }
    };
    this.dialogService.showDialog('confirm', 'body', param);
  }

  cancelstop(): void {
    this.raiseConfirm = false;
    this.dialogRef.close();
  }

  stopEndpoint(): void {
    this.actionMsg = 'stopping...';
    const configuration = this.getTestConfig('stop');
    if (configuration) {
      this.controllerApiService
        .setJMEPConfig({ reqData: configuration })
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(response => {
          if (response.status === 'success') {
            this.deviceService.getReflectorDevices();
            this.displayStopStatus(response);
          } else {
            this.setErrorMessage('Could not stop reflector: ' + response.data.message);
          }
        });
    } else {
      this.setErrorMessage('Cannot stop. I have no schedule ID for ' + this.deviceData.name);
    }
  }

  // Only called when the test successfully stopped or the test itself
  // told us it is not running anymore.
  // Failure to get a valid status response (response.status from UIComm != success) should not call this.
  //eslint-disable-next-line
  displayStopStatus(response: any): void {
    if (response.status === 'success') {
      this.setSuccessMessage('Reflector is no longer running');
    } else {
      this.setErrorMessage(response.data.message);
    }
  }

  setErrorMessage(message: string): void {
    if (message) {
      message = message.replace('$$20110$$', ''); //remove error code holder
    }
    this.actionMsg = null;
    this.errorIcon = 'warning';
    this.errorMsg = message;
  }

  setSuccessMessage(message: string): void {
    this.actionMsg = null;
    this.errorIcon = 'check_circle';
    this.errorMsg = message;
  }

  okClicked(): void {
    this.actionMsg = null;
    this.errorMsg = null;
  }

  confirmationDialogokClicked(): void {
    this.actionMsg = null;
    this.errorMsg = null;
    this.raiseConfirm = false;
    this.isTestRunning = false;
  }

  isEnabledJMEPLicencse(): void {
    this.controllerApiService
      .getLicense()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(response => {
        this.isLicenseLoaded = true;
        if (response.status !== 'error') {
          if (response.data.licenses && response.data.licenses.length > 0) {
            this.jmepLicenseObj = response.data.licenses.find(
              o => o.featureName === 'EAX-TWAMP-JMEP'
            );
            if (this.jmepLicenseObj) {
              this.jmepLicenseEnabled = true;
            }
          }
        } else {
          this.dialogService.showInfo(
            'information',
            'body',
            'License service not available: ' + response.data.message,
            'Error - License service not available'
          );
          this.dialogRef.close();
        }
      });
  }
}
